import { ref, onUnmounted } from 'vue'

// ---
// Types
// ---

type RunStatusData = Record<string, unknown>
type RunMetricsData = Record<string, unknown>

export type RunSocketHandlers = {
  onStatus?: (data: RunStatusData) => void
  /**
   * Called with one or more log lines. The CP coalesces a whole HTTP log
   * batch into a single WebSocket message (may contain `\n`-separated
   * lines), so consumers receive an array of lines rather than one
   * callback per line. Empty lines are filtered out.
   */
  onLog?: (lines: string[]) => void
  onMetrics?: (data: RunMetricsData) => void
}

type ServerMessage = {
  type: 'run:status' | 'run:log' | 'run:metrics'
  topic?: string
  runId?: number
  data: unknown
}

// ---
// Shared singleton connection
// ---

let ws: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectDelay = 1000
const MAX_RECONNECT_DELAY = 15000
const subscribers = new Map<string, Set<RunSocketHandlers>>()
const connected = ref(false)

function getWsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}/api/ws`
}

function sendSubscribe(topic: string): void {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'subscribe', topic }))
  }
}

function sendUnsubscribe(topic: string): void {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'unsubscribe', topic }))
  }
}

function handleMessage(event: MessageEvent): void {
  let msg: ServerMessage
  try {
    msg = JSON.parse(event.data) as ServerMessage
  } catch {
    return
  }

  const topic = msg.topic ?? (msg.runId != null ? `run:${msg.runId}` : null)
  if (!topic) return

  const handlers = subscribers.get(topic)
  if (!handlers) return

  for (const h of handlers) {
    switch (msg.type) {
      case 'run:status':
        h.onStatus?.(msg.data as RunStatusData)
        break
      case 'run:log': {
        // The CP sends a single batched payload that may contain many
        // lines separated by `\n`. Split here so consumers don't have
        // to know about the wire format.
        const raw = msg.data as string
        if (!raw) break
        const lines = raw.split('\n').filter(l => l.length > 0)
        if (lines.length > 0) h.onLog?.(lines)
        break
      }
      case 'run:metrics':
        h.onMetrics?.(msg.data as RunMetricsData)
        break
    }
  }
}

function connect(): void {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return
  }

  const socket = new WebSocket(getWsUrl())
  ws = socket

  socket.onopen = () => {
    if (ws !== socket) return
    connected.value = true
    reconnectDelay = 1000
    for (const topic of subscribers.keys()) {
      sendSubscribe(topic)
    }
  }

  socket.onmessage = handleMessage

  socket.onclose = () => {
    if (ws !== socket) return
    connected.value = false
    ws = null
    scheduleReconnect()
  }

  socket.onerror = () => {
    // onclose will fire after this
  }
}

function scheduleReconnect(): void {
  if (subscribers.size === 0) return
  if (reconnectTimer) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    connect()
    reconnectDelay = Math.min(reconnectDelay * 1.5, MAX_RECONNECT_DELAY)
  }, reconnectDelay)
}

function maybeDisconnect(): void {
  if (subscribers.size === 0) {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (ws) {
      ws.close()
      ws = null
      connected.value = false
    }
  }
}

// ---
// Public API
// ---

export function useRunSocket() {
  const localSubs: Array<{ topic: string; handlers: RunSocketHandlers }> = []

  function subscribeTopic(topic: string, handlers: RunSocketHandlers): void {
    let set = subscribers.get(topic)
    const isNew = !set
    if (!set) {
      set = new Set()
      subscribers.set(topic, set)
    }
    set.add(handlers)
    localSubs.push({ topic, handlers })

    connect()
    if (isNew) {
      sendSubscribe(topic)
    }
  }

  function subscribe(runId: number, handlers: RunSocketHandlers): void {
    subscribeTopic(`run:${runId}`, handlers)
  }

  function unsubscribeTopic(topic: string, handlers: RunSocketHandlers): void {
    const set = subscribers.get(topic)
    if (set) {
      set.delete(handlers)
      if (set.size === 0) {
        subscribers.delete(topic)
        sendUnsubscribe(topic)
      }
    }
    const idx = localSubs.findIndex(s => s.topic === topic && s.handlers === handlers)
    if (idx !== -1) localSubs.splice(idx, 1)

    maybeDisconnect()
  }

  function unsubscribe(runId: number, handlers: RunSocketHandlers): void {
    unsubscribeTopic(`run:${runId}`, handlers)
  }

  function unsubscribeAll(): void {
    for (const { topic, handlers } of [...localSubs]) {
      unsubscribeTopic(topic, handlers)
    }
  }

  onUnmounted(unsubscribeAll)

  return { subscribe, unsubscribe, subscribeTopic, unsubscribeTopic, unsubscribeAll, connected }
}
